import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Avatar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import LoginIcon from "@mui/icons-material/Login";
import axios from "axios";

const StudioDisplay = () => {
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [loadedOnce, setLoadedOnce] = useState(false);
  const navigate = useNavigate();

  // ✅ Clear only specific localStorage keys (not all)
  useEffect(() => {
    localStorage.removeItem("clientMongoId");
    localStorage.removeItem("studioName");
  }, []);

  // ✅ Fetch all studios (only once)
  useEffect(() => {
    if (loadedOnce) return; // prevent double fetch in React StrictMode
    async function fetchStudios() {
      try {
        const res = await axios.get("https://studio-lc.onrender.com/api/clients");
        // ✅ Remove duplicates using _id
        const uniqueStudios = Array.from(
          new Map(res.data.map((s) => [s._id, s])).values()
        );
        setStudios(uniqueStudios);
      } catch (err) {
        setStudios([]);
      }
      setLoadedOnce(true);
      setLoading(false);
    }
    fetchStudios();
  }, [loadedOnce]);

  const handleStudioClick = (studio) => {
    localStorage.setItem("clientMongoId", studio._id);
    localStorage.setItem("studioName", studio.studio);
    navigate("/user-home");
  };

  // ✅ Filter studios by search
  const filteredStudios = studios.filter(
    (studio) =>
      (studio.name && studio.name.toLowerCase().includes(search.toLowerCase())) ||
      (studio.studio && studio.studio.toLowerCase().includes(search.toLowerCase())) ||
      (studio.place && studio.place.toLowerCase().includes(search.toLowerCase())) ||
      (studio.location && studio.location.toLowerCase().includes(search.toLowerCase()))
  );

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        p: { xs: 2, md: 6 },
        backgroundImage: `url('https://wallpaperaccess.com/full/249925.jpg')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        position: "relative",
      }}
    >
      {/* ✅ Login Button */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 8, sm: 20 },
          right: { xs: 8, sm: 24 },
          zIndex: 10,
        }}
      >
        <IconButton
          onClick={() => navigate("/login")}
          sx={{
            bgcolor: "#fff",
            boxShadow: 2,
            "&:hover": { bgcolor: "#f9efcd" },
            width: 48,
            height: 48,
          }}
        >
          <Avatar sx={{ bgcolor: "#2575fc", width: 36, height: 36 }}>
            <LoginIcon sx={{ color: "#fff" }} />
          </Avatar>
        </IconButton>
      </Box>

      {/* ✅ Title */}
      <Box sx={{ borderRadius: 4, p: { xs: 1, md: 3 }, mb: 3 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            textAlign: "center",
            fontFamily: "Josefin Sans",
            mb: 2,
            background: "#fff",
            width: { xs: "70%", sm: "50%", md: "35%" },
            borderRadius: 3,
            ml: { xs: "15%", sm: "25%", md: "32%" },
            color: "#a67c52",
            whiteSpace: "nowrap",
            fontSize: { xs: 30, sm: 28, md: 36 },
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Select a Studio
        </Typography>

        {/* ✅ Search Bar */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search studio by name or place..."
            variant="outlined"
            sx={{
              width: { xs: "100%", sm: 400 },
              bgcolor: "#fff",
              borderRadius: 3,
              boxShadow: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                fontWeight: 500,
                fontSize: 18,
                color: "#a67c52",
                "& fieldset": { borderColor: "#ffe0b2" },
                "&:hover fieldset": { borderColor: "#a67c52" },
                "&.Mui-focused fieldset": {
                  borderColor: "#a67c52",
                  borderWidth: 2,
                },
              },
              "& input": { py: 1.2 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton edge="start" disabled>
                    <SearchIcon sx={{ color: "#a67c52", fontSize: 28 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* ✅ Grid of Cards */}
        <Grid container spacing={4} justifyContent="center" alignItems="stretch">
          {filteredStudios.map((studio) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4} // ✅ 3 cards per row
              key={studio._id}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <Card
                sx={{
                  width: 360,
                  height: 420,
                  borderRadius: 4,
                  boxShadow: 6,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  overflow: "hidden",
                  bgcolor: "#fff",
                }}
              >
                <CardActionArea
                  onClick={() => handleStudioClick(studio)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    height: "100%",
                  }}
                >
                  {/* ✅ Studio Logo */}
                  <Box
                    sx={{
                      width: "100%",
                      height: 180,
                      overflow: "hidden",
                      bgcolor: "#eee",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={
                        studio.studiologo
                          ? studio.studiologo
                          : "https://via.placeholder.com/480x180?text=Logo"
                      }
                      alt="Studio Logo"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>

                  {/* ✅ Card Details */}
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      textAlign: "center",
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#a67c52",
                        mb: 1,
                        fontFamily: "Josefin Sans",
                      }}
                    >
                      {studio.studio || studio.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#888", mb: 1, fontFamily: "Josefin Sans" }}
                    >
                      {studio.place || studio.location || ""}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#2575fc", mb: 2, fontFamily: "Josefin Sans" }}
                    >
                      {studio.mobile || ""}
                    </Typography>

                    {/* ✅ Thumbnails */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        mt: "auto",
                      }}
                    >
                      {Array.isArray(studio.studioimages) &&
                        studio.studioimages.slice(0, 5).map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`studio-img-${idx}`}
                            style={{
                              width: 42,
                              height: 42,
                              objectFit: "cover",
                              borderRadius: 6,
                              boxShadow: "0 2px 8px #a67c5233",
                              border: "2px solid #ffe0b2",
                            }}
                          />
                        ))}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default StudioDisplay;
